import '../App.css';
import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { T, US_STATES } from '../tokens';
import { getCurrentWeather, getCurrentLocation } from '../Utils/weather.tsx';
import SurveyData from '../Classes/Interfaces/SurveyData';
import SurveyUnits from '../Classes/Interfaces/SurveyUnits';

// npm install react-chartjs-2 chart.js MUST RUN THIS COMMAND TO USE CHART.JS COMPONENTS
const DEFAULT_NUM_RECORDERS = 1;
const DEFAULT_NUM_SECTIONS = 5;
const MIN_RECORDERS = 1;
const MAX_RECORDERS = 10;
const MIN_SECTIONS = 1;
const MAX_SECTIONS = 15;

const initialSurveyData: SurveyData = {
  name: "", type: "pollard", numSections: 0, sections: [], description: "",
  numRecorders: 0, recorderNames: [], location: "", windSpeed: 0,
  cloudCoverage: 0, temperature: 0, latitude: 0.0, longitude: 0.0, 
};

const initialSurveyUnits: SurveyUnits = { windSpeedUnit: "kph", temperatureUnit: "celsius" };

// ─── Icons ─────────────────────────────────────────────────────────────
const ArrowLeft = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ArrowRight = () => (
  <svg width={20} height={20} viewBox="0 0 24 24" fill="none">
    <path d="M9 6l6 6-6 6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const CloudIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <path d="M7 18h10a4 4 0 00.5-7.95A6 6 0 005.5 11 4 4 0 007 18z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);
const WindIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <path d="M3 8h11a3 3 0 100-6M3 16h15a3 3 0 110 6M3 12h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const ThermoIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <path d="M14 14V5a2 2 0 10-4 0v9a4 4 0 104 0z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);
const PinIcon = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
    <path d="M12 22s7-7 7-12a7 7 0 10-14 0c0 5 7 12 7 12z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);
const LeafIcon = () => (
  <svg width={40} height={40} viewBox="0 0 24 24" fill="none">
    <path d="M20 4c0 9-5 16-14 16-1 0-2-.1-2.5-.3 0-9 5-15.7 14-15.7 1 0 2 .1 2.5 0z" stroke={T.forest} strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M4 20l10-10" stroke={T.forest} strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

// ─── Shared atoms ─────────────────────────────────────────────────────
function FieldLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
      <label style={{ fontFamily: T.font, fontSize: 14, fontWeight: 600, color: T.ink, letterSpacing: '0.01em' }}>
        {children}
      </label>
      {hint && <span style={{ fontFamily: T.font, fontSize: 12, color: T.ink3 }}>{hint}</span>}
    </div>
  );
}

function FieldWrap({ label, hint, children }: { label?: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      {label && <FieldLabel hint={hint}>{label}</FieldLabel>}
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = 'text', leadingIcon, style = {} }: any) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      background: T.card, borderRadius: T.r.md, border: `1px solid ${T.line}`,
      height: 54, paddingLeft: leadingIcon ? 14 : 16, ...style,
    }}>
      {leadingIcon && <span style={{ color: T.ink3, marginRight: 10, display: 'flex' }}>{leadingIcon}</span>}
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={{
        flex: 1, height: '100%', border: 'none', outline: 'none', background: 'transparent',
        fontFamily: T.font, fontSize: 17, color: T.ink, padding: '0 16px 0 0',
        letterSpacing: '-0.01em', minWidth: 0,
      }} />
    </div>
  );
}

function SelectInput({ value, onChange, children, leadingIcon }: any) {
  return (
    <div style={{
      position: 'relative', display: 'flex', alignItems: 'center',
      background: T.card, borderRadius: T.r.md, border: `1px solid ${T.line}`, height: 54,
      paddingLeft: leadingIcon ? 14 : 16, paddingRight: 14,
    }}>
      {leadingIcon && <span style={{ color: T.ink3, marginRight: 10, display: 'flex' }}>{leadingIcon}</span>}
      <select value={value} onChange={onChange} style={{
        flex: 1, height: '100%', border: 'none', outline: 'none', background: 'transparent',
        fontFamily: T.font, fontSize: 17, color: T.ink, appearance: 'none', WebkitAppearance: 'none',
        letterSpacing: '-0.01em', cursor: 'pointer', minWidth: 0,
      }}>
        {children}
      </select>
      <span style={{ color: T.ink3, pointerEvents: 'none', display: 'flex' }}>
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </div>
  );
}

function Stepper({ value, onChange, min = 0, max = 99 }: { value: number; onChange: (v: number) => void; min?: number; max?: number }) {
  const btn = (content: React.ReactNode, onClick: () => void, disabled: boolean) => (
    <button type="button" onClick={onClick} disabled={disabled} style={{
      width: 64, height: 64, border: `1px solid ${T.line}`,
      background: disabled ? T.bgDeep : T.card,
      color: disabled ? T.ink3 : T.ink, cursor: disabled ? 'default' : 'pointer',
      borderRadius: T.r.md, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{content}</button>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {btn(<svg width={20} height={20} viewBox="0 0 24 24" fill="none"><path d="M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>, () => onChange(Math.max(min, value - 1)), value <= min)}
      <div style={{
        flex: 1, height: 64, background: T.card, borderRadius: T.r.md, border: `1px solid ${T.line}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: T.fontDisplay, fontSize: 26, fontWeight: 600, color: T.ink, letterSpacing: '-0.02em',
      }}>{value}</div>
      {btn(<svg width={20} height={20} viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>, () => onChange(Math.min(max, value + 1)), value >= max)}
    </div>
  );
}

function Segmented({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <div style={{ display: 'flex', background: T.bgDeep, borderRadius: T.r.md, padding: 4, gap: 4 }}>
      {options.map(o => {
        const active = o.value === value;
        return (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} style={{
            flex: 1, height: 44, border: 'none',
            background: active ? T.card : 'transparent',
            color: active ? T.ink : T.ink2,
            fontFamily: T.font, fontSize: 15, fontWeight: active ? 600 : 500,
            borderRadius: T.r.sm, cursor: 'pointer',
            boxShadow: active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
            letterSpacing: '-0.01em',
          }}>{o.label}</button>
        );
      })}
    </div>
  );
}

// ─── Main form component ───────────────────────────────────────────────
const SurveyForm = () => {
  const [userId, setUserId] = useState<any>();
  const [step, setStep] = useState(0);
  const [surveyData, setSurveyData] = useState<SurveyData>(initialSurveyData);
  const [surveyUnits, setSurveyUnits] = useState<SurveyUnits>(initialSurveyUnits);
  const [locationWorking, setLocationWorking] = useState<'unknown' | 'working' | 'not-working'>('unknown');

  const navigate = useNavigate();

  const u = (key: string, value: any) => setSurveyData(prev => ({ ...prev, [key]: value }));

  const changeSurveyUnits = (key: string, value: string) =>
    setSurveyUnits(prev => ({ ...prev, [key]: value }));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let v: any = value;
    if (name === 'numRecorders') {
      const n = Math.min(MAX_RECORDERS, Math.max(MIN_RECORDERS, Number(value)));
      v = n;
      setSurveyData(prev => ({
        ...prev,
        numRecorders: n,
        recorderNames: prev.recorderNames.slice(0, n),
      }));
      return;
    }
    if (name === 'numSections') {
      const n = Math.min(MAX_SECTIONS, Math.max(MIN_SECTIONS, Number(value)));
      v = n;
      setSurveyData(prev => ({
        ...prev,
        numSections: n,
        sections: prev.sections.slice(0, n),
      }));
      return;
    }
    setSurveyData(prev => ({ ...prev, [name]: v }));
  };

  const submitSurvey = async () => {
    const coords = await getCurrentLocation();
    setLocationWorking(coords ? 'working' : 'not-working');
    const weatherStart = coords ? await getCurrentWeather(coords.latitude, coords.longitude) : null;

    const windSpeed =
      surveyUnits.windSpeedUnit === "mph"
        ? surveyData.windSpeed * 1.609
        : surveyData.windSpeed;

    const temperature =
      surveyUnits.temperatureUnit === "fahrenheit"
        ? ((surveyData.temperature - 32) / 1.8)
        : surveyData.temperature;

    const createSurveyRequest: any = {
      ...surveyData,
      windSpeed,
      temperature,
      weatherStart,
      latitude: coords?.latitude ?? null,
      longitude: coords?.longitude ?? null,
    };

    try {
      const response = await fetch(`api/surveys?userId=${userId}`, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(createSurveyRequest),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || `Failed to start survey: ${response.status}`);
      }

      const createdSurvey = await response.json();
      const surveyId = createdSurvey ?? null;
      localStorage.setItem("survey", JSON.stringify({ ...createSurveyRequest, surveyId }));
      navigate("/recording");
    } catch (err: any) {
      console.error(err);
      alert("Error starting survey: " + (err.message || err));
    }
  };

  const updateSectionName = (i: number, val: string) => {
    const s = [...surveyData.sections];
    s[i] = val;
    u('sections', s);
  };

  const updateRecorderName = (i: number, val: string) => {
    const r = [...surveyData.recorderNames];
    r[i] = val;
    u('recorderNames', r);
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") as string);
    if (user) { setUserId(user?.userId); } else { navigate("/"); }
    setSurveyData(prev => ({ ...prev, numRecorders: DEFAULT_NUM_RECORDERS }));
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        if (!userId) return;
        const response = await fetch(`api/users/${userId}/settings`);
        if (!response.ok) throw new Error("No settings found");
        const settings = await response.json();
        const ns = settings.numSections || DEFAULT_NUM_SECTIONS;
        setSurveyData(prev => ({
          ...prev,
          name: settings.defaultSurveyName || "",
          type: settings.surveyType || "pollard",
          location: settings.location || "",
          numSections: ns,
        }));
        setSurveyUnits(prev => ({
          temperatureUnit: settings.temperatureUnit === "F" ? "fahrenheit" : "celsius",
          windSpeedUnit: settings.windType === "mph" ? "mph" : "kph",
        }));
      } catch (err) {
        console.error("Failed to load settings:", err);
        setSurveyData(prev => ({ ...prev, numSections: DEFAULT_NUM_SECTIONS }));
      }
    };
    fetchSettings();
  }, [userId]);

  useEffect(() => {
    if (!navigator?.geolocation) {
      setLocationWorking('not-working');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => setLocationWorking('working'),
      () => setLocationWorking('not-working'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);


  const steps = [
    { id: 'basics', label: 'Basics' },
    { id: 'route', label: 'Route' },
    { id: 'team', label: 'Team' },
    { id: 'conditions', label: 'Conditions' },
  ];

  const canContinue = () => {
    if (step === 0) return surveyData.name.trim().length > 0;
    if (step === 3) return surveyData.location.length > 0;
    return true;
  };

  const advance = () => {
    if (step < steps.length - 1) setStep(step + 1);
    else submitSurvey();
  };

  return (
    <div className="lep-page">
      <div className="lep-page-inner">
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', padding: '54px 16px 12px', gap: 8, background: T.bg,
          position: 'sticky', top: 0, zIndex: 10,
        }}>
          <button type="button" onClick={() => step === 0 ? navigate('/menu') : setStep(step - 1)} style={{
            width: 40, height: 40, border: 'none', background: 'transparent',
            borderRadius: T.r.pill, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: T.ink, cursor: 'pointer', flexShrink: 0,
          }}><ArrowLeft /></button>
          <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
            <div style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 600, color: T.ink, letterSpacing: '-0.01em' }}>New survey</div>
            <div style={{ fontFamily: T.font, fontSize: 12, color: T.ink2, marginTop: 1 }}>Step {step + 1} of {steps.length} · {steps[step].label}</div>
          </div>
          <div style={{ width: 40 }} />
        </div>

        {/* Progress bar */}
        <div style={{ padding: '0 20px 20px', display: 'flex', gap: 6 }}>
          {steps.map((s, i) => (
            <div key={s.id} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: i <= step ? T.forest : T.bgDeep,
              transition: 'background 0.2s',
            }} />
          ))}
        </div>

        {/* Step content */}
        <div style={{ padding: '0 20px', flex: 1, overflowY: 'auto', paddingBottom: 120 }}>

          {/* Step 0: Basics */}
          {step === 0 && (
            <>
              <FieldWrap label="Survey name">
                <TextInput
                  value={surveyData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => u('name', e.target.value)}
                  placeholder="e.g. Neal Smith Refuge — May"
                />
              </FieldWrap>

              <FieldWrap label="Survey type">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { v: 'pollard', t: 'Pollard transect', d: 'Walk fixed sections, count by section' },
                    { v: 'meandering', t: 'Meandering', d: 'Walk freely, single tally' },
                    { v: 'incidental', t: 'Incidental', d: 'Quick sightings only, no team' },
                  ].map(o => {
                    const active = surveyData.type === o.v;
                    return (
                      <button key={o.v} type="button" onClick={() => u('type', o.v)} style={{
                        padding: '14px 16px', borderRadius: T.r.md, textAlign: 'left',
                        background: active ? T.forestSoft : T.card,
                        border: `1.5px solid ${active ? T.forest : T.line}`,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
                      }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: 9999, flexShrink: 0,
                          border: `2px solid ${active ? T.forest : T.lineStrong}`,
                          background: active ? T.forest : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {active && <div style={{ width: 8, height: 8, borderRadius: 9999, background: '#fff' }} />}
                        </div>
                        <div>
                          <div style={{ fontFamily: T.font, fontSize: 15, fontWeight: 600, color: T.ink }}>{o.t}</div>
                          <div style={{ fontFamily: T.font, fontSize: 13, color: T.ink2, marginTop: 1 }}>{o.d}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </FieldWrap>

              <FieldWrap label="Description" hint="Optional">
                <textarea
                  name="description"
                  value={surveyData.description}
                  onChange={handleChange}
                  placeholder="Notes about today's visit…"
                  rows={3}
                  style={{
                    width: '100%', padding: 16,
                    background: T.card, border: `1px solid ${T.line}`, borderRadius: T.r.md,
                    fontFamily: T.font, fontSize: 16, color: T.ink, outline: 'none', resize: 'vertical',
                  }}
                />
              </FieldWrap>
            </>
          )}

          {/* Step 1: Route */}
          {step === 1 && surveyData.type === 'pollard' && (
            <>
              <FieldWrap label="Number of sections" hint={`A through ${String.fromCharCode(64 + (surveyData.numSections || 1))}`}>
                <Stepper
                  value={surveyData.numSections || DEFAULT_NUM_SECTIONS}
                  onChange={v => setSurveyData(prev => ({ ...prev, numSections: v, sections: prev.sections.slice(0, v) }))}
                  min={MIN_SECTIONS} max={MAX_SECTIONS}
                />
              </FieldWrap>
              <FieldWrap label="Section names" hint="Optional">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {Array.from({ length: surveyData.numSections || DEFAULT_NUM_SECTIONS }).map((_, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 44, height: 54, borderRadius: T.r.md, background: T.forestSoft,
                        color: T.forest, fontFamily: T.fontDisplay, fontSize: 18, fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>{String.fromCharCode(65 + i)}</div>
                      <div style={{ flex: 1 }}>
                        <TextInput
                          value={surveyData.sections[i] || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateSectionName(i, e.target.value)}
                          placeholder={`Section ${String.fromCharCode(65 + i)}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </FieldWrap>
            </>
          )}
          {step === 1 && surveyData.type !== 'pollard' && (
            <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: T.r.lg, padding: 32, textAlign: 'center' }}>
              <LeafIcon />
              <div style={{ fontFamily: T.fontDisplay, fontSize: 18, fontWeight: 600, color: T.ink, marginTop: 12 }}>No route needed</div>
              <div style={{ fontFamily: T.font, fontSize: 14, color: T.ink2, marginTop: 6 }}>
                {surveyData.type === 'meandering'
                  ? 'Meandering surveys are walked freely, with one combined tally.'
                  : 'Incidental surveys are quick — just record what you see.'}
              </div>
            </div>
          )}

          {/* Step 2: Team */}
          {step === 2 && surveyData.type !== 'incidental' && (
            <>
              <FieldWrap label="Number of recorders">
                <Stepper
                  value={surveyData.numRecorders || DEFAULT_NUM_RECORDERS}
                  onChange={v => setSurveyData(prev => ({ ...prev, numRecorders: v, recorderNames: prev.recorderNames.slice(0, v) }))}
                  min={MIN_RECORDERS} max={MAX_RECORDERS}
                />
              </FieldWrap>
              <FieldWrap label="Recorder names">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {Array.from({ length: surveyData.numRecorders || DEFAULT_NUM_RECORDERS }).map((_, i) => (
                    <TextInput
                      key={i}
                      value={surveyData.recorderNames[i] || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateRecorderName(i, e.target.value)}
                      placeholder={`Recorder ${i + 1}`}
                      leadingIcon={
                        <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      }
                    />
                  ))}
                </div>
              </FieldWrap>
            </>
          )}
          {step === 2 && surveyData.type === 'incidental' && (
            <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: T.r.lg, padding: 32, textAlign: 'center' }}>
              <div style={{ fontFamily: T.fontDisplay, fontSize: 18, fontWeight: 600, color: T.ink }}>Solo survey</div>
              <div style={{ fontFamily: T.font, fontSize: 14, color: T.ink2, marginTop: 6 }}>Incidental sightings are recorded by you alone.</div>
            </div>
          )}

          {/* Step 3: Conditions */}
          {step === 3 && (
            <>
              <div style={{ marginBottom: 14, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, borderRadius: T.r.md, background: locationWorking === 'working' ? '#E7F3FF' : locationWorking === 'not-working' ? '#FEEAEA' : T.card, border: `1px solid ${locationWorking === 'working' ? '#7DB7FF' : locationWorking === 'not-working' ? '#E08A8A' : T.line}`, color: locationWorking === 'working' ? '#1B5E99' : locationWorking === 'not-working' ? '#8F2626' : T.ink2 }}>
                <span>{locationWorking === 'working' ? 'Location working' : locationWorking === 'not-working' ? 'Location not working' : 'Checking location…'}</span>
                {locationWorking === 'not-working' && <span style={{ fontSize: 12, opacity: 0.85 }}>Enable GPS or allow location access.</span>}
              </div>
              <FieldWrap label="Location">
                <SelectInput
                  value={surveyData.location}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => u('location', e.target.value)}
                  leadingIcon={<PinIcon />}
                >
                  <option value="" disabled>Select a state</option>
                  {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                </SelectInput>
              </FieldWrap>

              {surveyData.type !== 'incidental' && (
                <>
                  <FieldWrap label="Wind speed">
                    <div style={{ display: 'flex', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <TextInput
                          value={surveyData.windSpeed || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => u('windSpeed', Number(e.target.value))}
                          type="number"
                          leadingIcon={<WindIcon />}
                          placeholder="0"
                        />
                      </div>
                      <Segmented
                        value={surveyUnits.windSpeedUnit}
                        onChange={v => changeSurveyUnits('windSpeedUnit', v)}
                        options={[{ value: 'kph', label: 'kph' }, { value: 'mph', label: 'mph' }]}
                      />
                    </div>
                  </FieldWrap>

                  <FieldWrap label={`Cloud cover · ${surveyData.cloudCoverage}%`}>
                    <div style={{ background: T.card, border: `1px solid ${T.line}`, borderRadius: T.r.md, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ color: T.ink3 }}><CloudIcon /></span>
                      <input
                        type="range" min="0" max="100" step="10"
                        value={surveyData.cloudCoverage}
                        onChange={(e) => u('cloudCoverage', Number(e.target.value))}
                        style={{ flex: 1, height: 28 }}
                      />
                      <span style={{ fontFamily: T.fontMono, fontSize: 14, color: T.ink2, minWidth: 38, textAlign: 'right' }}>{surveyData.cloudCoverage}%</span>
                    </div>
                  </FieldWrap>

                  <FieldWrap label="Temperature">
                    <div style={{ display: 'flex', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <TextInput
                          value={surveyData.temperature || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => u('temperature', Number(e.target.value))}
                          type="number"
                          leadingIcon={<ThermoIcon />}
                          placeholder="0"
                        />
                      </div>
                      <Segmented
                        value={surveyUnits.temperatureUnit}
                        onChange={v => changeSurveyUnits('temperatureUnit', v)}
                        options={[{ value: 'celsius', label: '°C' }, { value: 'fahrenheit', label: '°F' }]}
                      />
                    </div>
                  </FieldWrap>
                </>
              )}
            </>
          )}
        </div>

        {/* Sticky CTA */}
        <div className="lep-sticky-footer">
          <button
            type="button"
            onClick={advance}
            disabled={!canContinue()}
            style={{
              width: '100%', height: 56, border: 'none',
              background: canContinue() ? T.forest : T.bgDeep,
              color: canContinue() ? '#fff' : T.ink3,
              borderRadius: T.r.md, fontFamily: T.font, fontSize: 17, fontWeight: 600,
              letterSpacing: '-0.01em', cursor: canContinue() ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: canContinue() ? '0 1px 0 rgba(0,0,0,0.04), 0 4px 14px rgba(45,80,22,0.18)' : 'none',
            }}
          >
            <span>{step < steps.length - 1 ? 'Continue' : 'Start survey'}</span>
            {canContinue() && <ArrowRight />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyForm;
